import React, { useState, useEffect, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Media } from 'reactstrap';
import DataTable from '../../Common/DataTable';
import { PlusCircle, Edit, Key } from 'react-feather';
import EmployeeDataModal from './EmployeeDataModal';
import PasswordModal from './PasswordModal';
import { fetchTableData } from '../../../Api/ApiMethods';

const Employees = () => {
    // selectedRows, clearSelectedRows, deleteModal states removed - no selection/deletion needed
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [selectedEmployeeForPassword, setSelectedEmployeeForPassword] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const employeeApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/user';

    // handleRowSelected removed - no selection needed

    useEffect(() => {
        let isMounted = true;
        const loadEmployees = async () => {
            setIsLoading(true);
            try {
                await fetchTableData({
                    apiEndpoint: employeeApiUrl,
                    page,
                    pageSize,
                    setDataCallback: setData,
                    setTotalRowsCallback: setTotalRows,
                });
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadEmployees();

        return () => {
            isMounted = false;
        };
    }, [page, pageSize, employeeApiUrl]);

    // toggleDeleteModal removed - no deletion needed

    const openAddModal = () => {
        setModalMode('add');
        setSelectedEmployee(null);
        setModalOpen(true);
    };

    const openModifyModal = (employee) => {
        setModalMode('modify');
        setSelectedEmployee(employee);
        setModalOpen(true);
    };

    const openPasswordModal = (employee) => {
        setSelectedEmployeeForPassword(employee);
        setPasswordModalOpen(true);
    };

    const togglePasswordModal = () => {
        setPasswordModalOpen(!passwordModalOpen);
        if (passwordModalOpen) {
            setTimeout(() => setSelectedEmployeeForPassword(null), 300);
        }
    };

    const columns = [
        {
            name: 'Roller',
            sortable: true,
            cell: (row) => {
                // Yeni API yapısına göre roles artık bir obje
                if (!row.roles || typeof row.roles !== 'object') {
                    return '-';
                }
                
                // Roller objesinden değerleri (rol isimlerini) al
                const roleNames = Object.values(row.roles).join(', ');
                
                return <div title={roleNames}>{roleNames}</div>;
            }
        },
        { name: 'Ad', selector: row => row.firstName, sortable: true },
        { name: 'Soyad', selector: row => row.lastName, sortable: true },
        { name: 'Kimlik Numarası', selector: row => row.tcId, sortable: true },
        { name: 'İş', selector: row => row.job, sortable: true },
        { name: 'Departman', selector: row => row.department, sortable: true },
        { name: 'Telefon', selector: row => row.phoneNumber, sortable: true },
        { name: 'E-posta', selector: row => row.email, sortable: true },
        { name: 'İşe Başlama Tarihi', selector: row => row.employmentStartDate ? new Date(row.employmentStartDate).toLocaleDateString() : '-', sortable: true },
        { name: 'Bloklu Kullanıcı', selector: row => row.isBlocked ? 'Evet' : 'Hayır', sortable: true },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div title="Düzenle"
                    className="btn btn-outline-warning"
                    onClick={() => openModifyModal(row)}>
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true
        },
        {
            name: 'Parola Tanımla',
            cell: (row) => (
                <div title="Parola Tanımla"
                    className="btn btn-outline-success"
                    onClick={() => openPasswordModal(row)}
                >
                    <Key size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true
        },
    ];

    return (
        <>
            <Breadcrumbs parent='Yönetim Alanları' title='Çalışanlar' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Çalışanlar</H5>
                                <span>Tanımlanmış olan çalışanlar aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <Media body className='text-end mt-3'>
                                        <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1" />
                                            {' Personel Ekle'}
                                        </div>
                                    </Media>
                                    {/* Delete button removed - no deletion allowed */}
                                </div>

                                <div style={{ height: "40px" }}></div>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        pagination
                                        paginationServer
                                        stickyActionPosition="left"
                                        paginationTotalRows={totalRows}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => setPageSize(pageSize)}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : "Çalışan bulunamadı."}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <EmployeeDataModal
                apiEndpoint={employeeApiUrl}
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                mode={modalMode}
                selectedEmployee={selectedEmployee}
                refreshData={() =>
                    fetchTableData({
                        apiEndpoint: employeeApiUrl,
                        page,
                        pageSize,
                        setDataCallback: setData,
                        setTotalRowsCallback: setTotalRows,
                    })
                }
            />
            {/* DeleteConfirmationModal removed - no deletion allowed */}
            <PasswordModal
                isOpen={passwordModalOpen}
                toggle={togglePasswordModal}
                employee={selectedEmployeeForPassword}
                apiEndpoint={employeeApiUrl}
            />
        </>
    );
};

export default Employees;
